import { useMemo } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmtDate } from "@/lib/checkins";
import { CHART_COLORS } from "../staff/DiaryTrendChart";

/** One chart per diary type: each diary's average for every 0–10 score, oldest to newest. Blanks are skipped. */
function seriesFor(rows) {
  const sorted = [...rows].sort((a, b) => String(a.period?.start).localeCompare(String(b.period?.start)));
  const fields = [];
  sorted.forEach((r) => (r.averages || []).forEach((a) => {
    const at = fields.findIndex((f) => f.code === a.code);
    if (at < 0) fields.push({ code: a.code, label: a.label }); else fields[at].label = a.label;
  }));
  const data = sorted.map((r) => ({ when: fmtDate(r.period?.start),
    ...Object.fromEntries((r.averages || []).filter((a) => a.mean != null).map((a) => [a.code, a.mean])) }));
  return { fields, data };
}

function AveragesChart({ title, rows, tid }) {
  const { fields, data } = useMemo(() => seriesFor(rows), [rows]);
  if (!fields.length) return null;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3" data-testid={`diary-averages-${tid}`}>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-600">
        <span className="font-semibold text-zinc-950" data-testid={`diary-averages-${tid}-title`}>{title} — average per diary</span>
        {fields.map((f, i) => (
          <span key={f.code} className="inline-flex items-center gap-1.5" data-testid={`diary-averages-${tid}-legend-${f.code}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{f.label}
          </span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
          <CartesianGrid stroke="#f4f4f5" />
          <XAxis dataKey="when" tick={{ fontSize: 11 }} />
          <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v, k) => [v, fields.find((f) => f.code === k)?.label || k]} />
          {fields.map((f, i) => <Line key={f.code} dataKey={f.code} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls isAnimationActive={false} />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DiaryAverages({ rows }) {
  const groups = useMemo(() => Object.values(rows.reduce((acc, r) => {
    (acc[r.template_id] = acc[r.template_id] || { id: r.template_id, title: r.template_name, rows: [] }).rows.push(r);
    return acc;
  }, {})), [rows]);
  return groups.filter((g) => g.rows.length >= 2).map((g) => <AveragesChart key={g.id} title={g.title || "Diary"} rows={g.rows} tid={g.id} />);
}
